import { useEffect, useState } from "react";
import { Heart, PenLine, Share2, Copy, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";

// Sticky action bar for memorial detail pages. Slides down from the
// top when the visitor scrolls past the hero portrait — mirrors
// Legacy.com's persistent action row but in our warmer voice.
//
// Actions:
//   • Leave a memory  — anchor jump to #leave-a-memory
//   • Follow / Following — toggles the follow via /memorials/:key/follow
//   • Share — navigator.share → clipboard fallback
//   • Copy link — always available as a discrete backup
export default function StickyMemorialActionBar({ memorial, onFollowChange }) {
  const [visible, setVisible] = useState(false);
  const [following, setFollowing] = useState(!!memorial?.is_following);
  const [busy, setBusy] = useState(false);

  useEffect(() => setFollowing(!!memorial?.is_following), [memorial?.is_following]);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 480);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!memorial) return null;

  const detailPath = `/celebrate/${memorial.slug || memorial.id}`;
  const shareUrl = `${window.location.origin}${detailPath}`;

  const toggleFollow = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/memorials/${memorial.slug || memorial.id}/${following ? "unfollow" : "follow"}`);
      const nextFollowing = !!(data?.following ?? data?.is_following);
      setFollowing(nextFollowing);
      onFollowChange?.(nextFollowing, data?.follower_count);
      toast.success(nextFollowing ? "Following — you'll be notified on anniversaries" : "Unfollowed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please sign in to follow this memorial.");
    } finally { setBusy(false); }
  };

  const share = async () => {
    const shareData = { title: memorial.full_name || "In loving memory", url: shareUrl };
    if (navigator.share) {
      try { await navigator.share(shareData); return; } catch { /* fall */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch { toast.error("Couldn't copy link"); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch { toast.error("Couldn't copy link"); }
  };

  return (
    <div
      data-testid="memorial-sticky-action-bar"
      className={`fixed left-0 right-0 top-0 z-40 border-b transition-transform duration-300 ${visible ? "translate-y-0" : "-translate-y-full"}`}
      style={{
        background: "rgba(255, 255, 255, 0.92)",
        backdropFilter: "blur(14px)",
        borderColor: "var(--wofy-border)",
      }}
    >
      <div className="max-w-6xl mx-auto px-4 lg:px-8 h-14 flex items-center gap-3">
        {/* Portrait thumb + name */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {memorial.portrait && (
            <img
              src={memorial.portrait}
              alt=""
              className="w-8 h-8 rounded-full object-cover shrink-0"
            />
          )}
          <div className="min-w-0">
            <div className="font-display text-sm leading-none truncate">
              {memorial.full_name || memorial.name_latin || "In loving memory"}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 truncate">
              {memorial.location?.city || memorial.death_place || memorial.location?.country || ""}
            </div>
          </div>
        </div>
        {/* Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          <a
            href="#leave-a-memory"
            data-testid="sticky-leave-memory"
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs md:text-sm"
            style={{ background: "var(--wofy-primary)", color: "#fff" }}
          >
            <PenLine className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Leave a memory</span>
            <span className="sm:hidden">Memory</span>
          </a>
          {/* Familiar-phrase alias for legacy visitors who look for
              "Sign the Guestbook". Same target, softer ghost styling. */}
          <a
            href="#leave-a-memory"
            data-testid="sticky-sign-guestbook"
            className="hidden lg:inline-flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-xs hover:bg-neutral-50"
            style={{ borderColor: "var(--wofy-border)" }}
          >
            Sign the guestbook
          </a>
          <button
            type="button"
            onClick={toggleFollow}
            disabled={busy}
            aria-pressed={following}
            data-testid="sticky-follow"
            className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-xs hover:bg-neutral-50"
            style={{ borderColor: "var(--wofy-border)", color: following ? "#DC2626" : "inherit" }}
            title={following ? "Unfollow" : "Follow — get notified on anniversaries"}
          >
            {following ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
            <span className="hidden md:inline">{following ? "Following" : "Follow"}</span>
          </button>
          <button
            type="button"
            onClick={share}
            data-testid="sticky-share"
            className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-xs hover:bg-neutral-50"
            style={{ borderColor: "var(--wofy-border)" }}
            title="Share"
          >
            <Share2 className="w-3.5 h-3.5" />
            <span className="sr-only">Share</span>
          </button>
          <button
            type="button"
            onClick={copyLink}
            data-testid="sticky-copy"
            className="hidden sm:inline-flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-xs hover:bg-neutral-50"
            style={{ borderColor: "var(--wofy-border)" }}
            title="Copy link"
          >
            <Copy className="w-3.5 h-3.5" />
            <span className="sr-only">Copy link</span>
          </button>
          <button
            type="button"
            aria-label="Loved"
            data-testid="sticky-love"
            className="hidden md:inline-flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-xs hover:bg-neutral-50"
            style={{ borderColor: "var(--wofy-border)" }}
            title="A quiet moment"
          >
            <Heart className="w-3.5 h-3.5" style={{ color: "#DC2626" }} />
          </button>
        </div>
      </div>
    </div>
  );
}
